import bridge from '@vkontakte/vk-bridge';

/** VK launch params may be in search or in hash (direct app links). */
export function getVkLaunchParams(): URLSearchParams {
  const fromSearch = new URLSearchParams(window.location.search);
  if (fromSearch.has('vk_app_id') || fromSearch.has('vk_platform')) {
    return fromSearch;
  }

  const rawHash = window.location.hash.replace(/^#/, '');
  if (rawHash.includes('vk_app_id') || rawHash.includes('vk_platform')) {
    const query = rawHash.includes('?') ? rawHash.split('?').pop()! : rawHash;
    return new URLSearchParams(query);
  }

  return fromSearch;
}

export function isVkMiniAppHost(): boolean {
  const params = getVkLaunchParams();
  if (params.has('vk_platform') || params.has('vk_app_id')) {
    return true;
  }

  const host = window.location.hostname;
  return host.includes('vk.com') || host.endsWith('.vk-apps.com');
}

async function sendBridge<T>(method: string, params?: object): Promise<T | null> {
  if (!isVkMiniAppHost()) return null;
  try {
    return (await bridge.send(method as never, params as never)) as T;
  } catch {
    return null;
  }
}

export async function copyText(text: string): Promise<boolean> {
  const copied = await sendBridge<{ result: boolean }>('VKWebAppCopyText', {
    text,
  });
  if (copied?.result) return true;

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Open external URL (registration site, simulator download, etc.). */
export async function openUrl(url: string): Promise<boolean> {
  const target = url?.trim();
  if (!target) return false;

  const inVk = isVkMiniAppHost();

  if (inVk) {
    try {
      await bridge.send('VKWebAppOpenURL', { url: target });
      return true;
    } catch {
      // fall through — window.open is often blocked in VK WebView
    }
  }

  try {
    const link = document.createElement('a');
    link.href = target;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    return true;
  } catch {
    // continue
  }

  if (!inVk) {
    const win = window.open(target, '_blank', 'noopener,noreferrer');
    return win !== null;
  }

  return false;
}

export async function allowNotifications(): Promise<boolean> {
  const res = await sendBridge<{ result: boolean }>('VKWebAppAllowNotifications');
  return res?.result === true;
}
